import { APP_BOOTSTRAP_LISTENER, ApplicationConfig, ErrorHandler, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import {registerLocaleData} from '@angular/common';
import {ServiceWorkerModule} from '@angular/service-worker';
import {provideAnimations} from '@angular/platform-browser/animations';
import {BrowserModule} from '@angular/platform-browser';
import {Router, provideRouter} from '@angular/router';
import {HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient, withFetch} from '@angular/common/http';
import {MAT_TOOLTIP_DEFAULT_OPTIONS} from '@angular/material/tooltip';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {Store, StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {KeycloakService, KeycloakAngularModule} from 'keycloak-angular';
import localeFr from '@angular/common/locales/fr';
import * as Sentry from '@sentry/angular';

import {ErrorHandlerService} from '@shared/error-handling/error-handler.service';
import {profileAction} from '@redux/actions/profiles.action';
import {AppConfigurationService} from '@shared/app-configuration.service';
import {reducer, clearStateWhenChangingProfile, StoreState} from '@redux/reducers';
import {ProfileService} from '@services/profile.service';

import {environment} from '../environments/environment';
import {appRoutes} from './app.routes';
import {MaterialModule} from './material.module';
import {getMatPaginatorIntlFR} from './MatPaginatorFR';
import {CountRequestInterceptorService} from "@shared/count-request-interceptor.service";
import {CustomHttpHeaderInterceptorService} from "@shared/custom-http-header-interceptor.service";
import {BlockRequestInterceptorService} from "@shared/block-request-interceptor.service";
import {UsageMetricsService} from "@shared/usage-metrics/usage-metrics.service";

registerLocaleData(localeFr);

function initializer(keycloak: KeycloakService, profileService: ProfileService, store: Store<StoreState>, appConfigService: AppConfigurationService): () => Promise<void> {
  return (): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await keycloak.init({
          config: appConfigService.keycloakConfig,
          initOptions: {
            onLoad: 'login-required',
            checkLoginIframe: false,
            pkceMethod: 'S256'
          },
          enableBearerInterceptor: true,
          bearerExcludedUrls: [
            '/assets'
          ]
        });
        profileService.getProfiles()
          .subscribe(profiles => store.dispatch(profileAction.setProfiles({profiles, isCabangaLite: appConfigService.isCabangaLite})));
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };
}

function initializerSentry(appConfigService: AppConfigurationService) {
  const _config = appConfigService.getConfig();
  if (_config?.sentry.dsn) {
    Sentry.init({
      dsn: _config.sentry.dsn,
      environment: _config.envName,
      release: environment.gitTag || environment.gitCommitId,
      ignoreErrors: [
        'Non-Error exception captured'
      ],
      integrations: [
        // new Integrations.BrowserTracing({
        //   tracingOrigins: ['localhost:4200', 'https://yourserver.io/api'],
        //   routingInstrumentation: Sentry.routingInstrumentation,
        // }),
      ],
      tracesSampleRate: 0,
      // Removed
      // autoSessionTracking: _config?.sentry.autoSessionTracking,
      sampleRate: _config?.sentry.sampleRate,
      // Only explicit Sentry.logger calls are sent (usage tracking), console logs are not captured
      enableLogs: true
    });
  }
  return () => Promise.resolve();
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      StoreModule.forRoot(reducer, {
        metaReducers: [clearStateWhenChangingProfile],
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      }),
      !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 50 , connectInZone: true}) : [],
      BrowserModule,
      ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
      KeycloakAngularModule,
      MaterialModule
    ),
    AppConfigurationService,
    provideAppInitializer(() => {
        const initializerFn = (initializer)(inject(KeycloakService), inject(ProfileService), inject(Store), inject(AppConfigurationService));
        return initializerFn();
      }),
    {
      provide: MatPaginatorIntl,
      useValue: getMatPaginatorIntlFR()
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
        showDelay: 500,
        hideDelay: 50,
        touchendHideDelay: 1000,
      }
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CountRequestInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpHeaderInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BlockRequestInterceptorService,
      multi: true
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
        const initializerFn = (initializerSentry)(inject(AppConfigurationService));
        return initializerFn();
      }),
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: () => {
        const usageMetricsService = inject(UsageMetricsService);
        return () => usageMetricsService.start();
      }
    },
    { provide: LOCALE_ID, useValue: "fr-FR" },
    provideAnimations(),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi()
    ),
    provideRouter(appRoutes)
  ]
};
